import React, { FC, useEffect, useState } from "react";
import { Box, Typography, Button, useTheme, Slide } from "@mui/material";
import TennisTable from "./TennisTables";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import TablePlayers from "./TablePlayers";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
  fetchTables,
  getAvailableRooms,
  getAvailableRoomNames,
  getTablesWithMatchsGroupedByRows,
  listenToMatchs,
} from "../../features/matchs/matchsSlice";
import {
  fetchBoards,
  fetchTournoi,
  getBoards,
  getLiveRefreshPeriodInSeconds,
  getTournoi,
} from "../../features/tournoi/tournoiSlice";

interface LiveProps {}

const Live: FC<LiveProps> = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [roomIdx, setRoomIdx] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const tournoi = useAppSelector(getTournoi);
  const availableRooms = useAppSelector(getAvailableRooms);
  const availableRoomNames = useAppSelector(getAvailableRoomNames);
  const boards = useAppSelector(getBoards);
  const safeRoomIdx =
    availableRooms.length > 0 ? roomIdx % availableRooms.length : 0;
  const tablesByRow = useAppSelector((state: RootState) =>
    getTablesWithMatchsGroupedByRows(state, availableRooms[safeRoomIdx] ?? 1)
  );

  const refreshInterval = useAppSelector(getLiveRefreshPeriodInSeconds);

  useEffect(() => {
    if (!tournoi?.id) return;
    dispatch(fetchTables());
    dispatch(fetchBoards());
    const unsubscribe = dispatch(listenToMatchs());
    return () => {
      unsubscribe();
    };
  }, [dispatch, tournoi?.id]);

  useEffect(() => {
    if (availableRooms.length === 0) return;
    const interval = setInterval(() => {
      setRoomIdx((prev) => {
        const next = prev + 1;
        if (next < availableRooms.length) return next;
        dispatch(fetchTournoi());
        dispatch(fetchBoards());
        return 0;
      });
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [availableRooms.length, dispatch, refreshInterval]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      const fullscreenElement =
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement;
      setIsFullScreen(fullscreenElement != null);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange);
    document.addEventListener("mozfullscreenchange", handleFullScreenChange);
    document.addEventListener("MSFullscreenChange", handleFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullScreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullScreenChange
      );
    };
  }, []);

  const handleFullScreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if ((el as any).webkitRequestFullscreen)
      (el as any).webkitRequestFullscreen();
    else if ((el as any).mozRequestFullScreen)
      (el as any).mozRequestFullScreen();
    else if ((el as any).msRequestFullscreen) (el as any).msRequestFullscreen();
  };

  return (
    tablesByRow && (
      <Slide in direction="left" key={safeRoomIdx} timeout={500}>
        <Box
          sx={{
            bgcolor: theme.palette.background.default,
            height: "100vh",
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: isFullScreen ? "center" : "flex-start",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 10,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontWeight: "bold",
              boxShadow: 2,
              textAlign: "center",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
          >
            {availableRoomNames[safeRoomIdx] ||
              `Salle ${availableRooms[safeRoomIdx] ?? 1}`}
          </Typography>

          {!isFullScreen && (
            <Button
              variant="contained"
              onClick={handleFullScreen}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10,
              }}
            >
              Plein écran
            </Button>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              width: "100%",
              maxWidth: isFullScreen ? "100%" : "1200px",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Grid2
              container
              direction="column"
              sx={{
                flexGrow: 1,
                justifyContent: "space-evenly",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              {Object.keys(tablesByRow).map((row) => (
                <Grid2
                  container
                  key={row}
                  sx={{
                    flexGrow: 1,
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {tablesByRow[parseInt(row)].map((table, idx) => {
                    const liveMatch = table.matchs?.[0];
                    const boardColor = liveMatch
                      ? boards.find((b) => b.id === liveMatch.tableauId)
                          ?.liveColor
                      : undefined;

                    return (
                    <Grid2 key={idx} xs>
                      <TennisTable tableId={table.tableId!}>
                        {liveMatch?.matchsPlayerParticipations && (
                          <TablePlayers
                            matchPlayers={
                              liveMatch.matchsPlayerParticipations
                            }
                            boardColor={boardColor}
                          />
                        )}
                      </TennisTable>
                    </Grid2>
                    );
                  })}
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Box>
      </Slide>
    )
  );
};

export default Live;
